"use client"

import { useLazyGetInvoiceItemsForInvoiceQuery } from "@/services/tax/tax-workflow"
import { useEffect, useState, useMemo, useCallback } from "react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { formatToBRL, formatPercentage } from "@/utils/formatToMoney"
import { formatDateTime2 } from "@/utils/format-date"
import { NcmStatusBadge } from "./ncm-status-badge"
import type { InvoiceItem, InvoiceItemsModalProps } from "./types"

const ITEMS_PER_PAGE = 10

export function InvoiceItemsModal({ id }: InvoiceItemsModalProps) {
    const [getInvoiceItems, { data, isLoading, isError }] = useLazyGetInvoiceItemsForInvoiceQuery()
    const [page, setPage] = useState(1)

    useEffect(() => {
        if (id) {
            getInvoiceItems({ invoiceId: id })
        }
    }, [id, getInvoiceItems])

    const sortedData = useMemo<InvoiceItem[]>(() => {
        return Array.isArray(data)
            ? [...data].sort((a, b) => a.item_sequence - b.item_sequence)
            : []
    }, [data])

    const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE)

    const paginatedData = useMemo(() => {
        return sortedData.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)
    }, [sortedData, page])

    const handlePreviousPage = useCallback(() => {
        setPage((p) => Math.max(1, p - 1))
    }, [])

    const handleNextPage = useCallback(() => {
        setPage((p) => Math.min(totalPages, p + 1))
    }, [totalPages])

    const handlePageClick = useCallback((pageNum: number) => {
        setPage(pageNum)
    }, [])

    const visiblePages = useMemo(() => {
        const maxVisible = 3
        const start = Math.max(1, Math.min(page - 1, totalPages - maxVisible + 1))
        const end = Math.min(totalPages, start + maxVisible - 1)
        return Array.from({ length: end - start + 1 }, (_, i) => start + i)
    }, [page, totalPages])

    // Conditional returns AFTER all hooks
    if (!id) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="text-sm text-muted-foreground mb-1">Erro ao carregar</div>
                    <div className="text-xs text-muted-foreground">ID da nota fiscal não encontrado</div>
                </div>
            </div>
        )
    }

    if (isError) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="text-sm text-muted-foreground mb-1">Erro ao carregar itens</div>
                    <div className="text-xs text-muted-foreground">Não foi possível carregar os itens desta nota fiscal</div>
                </div>
            </div>
        )
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <div className="text-sm text-muted-foreground">Carregando itens...</div>
                </div>
            </div>
        )
    }

    if (!sortedData.length) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="text-sm text-muted-foreground mb-1">Nenhum item encontrado</div>
                    <div className="text-xs text-muted-foreground">Esta nota fiscal não possui itens cadastrados</div>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-sm font-medium">
                    {sortedData.length} {sortedData.length === 1 ? "item encontrado" : "itens encontrados"}
                </div>
            </div>

            <div className="rounded-lg border bg-card">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="hover:bg-transparent">
                                <TableHead className="font-semibold text-xs uppercase tracking-wide w-16">Seq.</TableHead>
                                <TableHead className="font-semibold text-xs uppercase tracking-wide">NCM</TableHead>
                                <TableHead className="font-semibold text-xs uppercase tracking-wide">Status NCM</TableHead>
                                <TableHead className="font-semibold text-xs uppercase tracking-wide">UF / Alíq. NCM</TableHead>
                                <TableHead className="font-semibold text-xs uppercase tracking-wide text-right">
                                    Valor Contábil
                                </TableHead>
                                <TableHead className="font-semibold text-xs uppercase tracking-wide text-right">
                                    Total Imposto
                                </TableHead>
                                <TableHead className="font-semibold text-xs uppercase tracking-wide text-right">Alíquota</TableHead>
                                <TableHead className="font-semibold text-xs uppercase tracking-wide">Criado</TableHead>
                                <TableHead className="font-semibold text-xs uppercase tracking-wide">Atualizado</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedData.map((invoiceItem) => (
                                <TableRow key={invoiceItem.id} className="hover:bg-muted/50">
                                    <TableCell className="font-medium">
                                        <span className="inline-flex items-center justify-center w-8 h-6 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold">
                                            {invoiceItem.item_sequence}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <code className="px-2 py-1 bg-muted rounded text-xs font-mono">{invoiceItem.ncm_code}</code>
                                    </TableCell>
                                    <TableCell className="min-w-[170px] flex">
                                        <NcmStatusBadge invoiceItem={invoiceItem} />
                                    </TableCell>
                                    <TableCell className="text-xs">
                                        {invoiceItem.ncmSt?.state ? (
                                            <span className="inline-flex items-center gap-1">
                                                <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold">
                                                    {invoiceItem.ncmSt.state}
                                                </span>
                                                {invoiceItem.ncmSt.iva && (
                                                    <span className="text-muted-foreground">
                                                        {formatPercentage(invoiceItem.ncmSt.iva)}
                                                    </span>
                                                )}
                                            </span>
                                        ) : (
                                            <span className="text-muted-foreground">—</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right font-medium">
                                        {formatToBRL(invoiceItem.accounting_value)}
                                    </TableCell>
                                    <TableCell className="text-right font-medium">
                                        {formatToBRL(invoiceItem.total_tax_value)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                                            {formatPercentage(invoiceItem.tax_rate)}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground text-xs">
                                        {formatDateTime2(invoiceItem.created_at)}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground text-xs">
                                        {formatDateTime2(invoiceItem.updated_at)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                        Mostrando {(page - 1) * ITEMS_PER_PAGE + 1} a {Math.min(page * ITEMS_PER_PAGE, sortedData.length)} de{" "}
                        {sortedData.length} itens
                    </div>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePreviousPage}
                            disabled={page === 1}
                            className="h-8 px-2"
                        >
                            <ChevronLeft className="h-3 w-3" />
                        </Button>
                        <div className="flex items-center gap-1">
                            {visiblePages.map((pageNum) => (
                                <Button
                                    key={pageNum}
                                    variant={page === pageNum ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => handlePageClick(pageNum)}
                                    className="w-7 h-7 p-0 text-xs"
                                >
                                    {pageNum}
                                </Button>
                            ))}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleNextPage}
                            disabled={page === totalPages}
                            className="h-8 px-2"
                        >
                            <ChevronRight className="h-3 w-3" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}
